import React, { useState, useRef, useContext, useMemo } from 'react';
import { Route, withRouter } from 'react-router';
import { withStyles } from '@material-ui/core';
import classNames from 'classnames';
import { ActionBar, BreadcrumbBar, NavigationTabs } from '@markinson/uicomponents-v2';
import { ILocationState, useModule } from 'utils/hooks';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { useTabLocations } from './ProductReceipts.hooks';
import { ILinesHandle } from './Lines/Lines.properties';
import { usePRContextSelector, usePRDispatch } from './ProductReceipts.context';
import SummaryPanel from './SummaryPanel';
import ProductReceiptsLookupPanel from './ProductReceiptsLookupPanel';
import { IProductReceiptsProps } from './ProductReceipts.properties';
import { DisabledChangeStatus, getTabs } from './ProductReceipts.constants';
import modules from './ProductReceipts.modules';
import Lines from './Lines';

// Update it with Product Receipt when doing api integration
import styles from './ProductReceipts.styles';

import { IProcessProductReceiptCriteriaFacade, IProductReceiptContextPageFacade, IValidatedResponse } from 'api/swaggerTypes';
import { useApplyProductReceiptContext, IObjectifiedProductReceiptContextResponse, useRetrieveParkedReceipts, useParkProductReceipt, useRetrieveProductReceiptSummary, useCancelProductReceipt, useReceiptConfirmation, useProcessReceipt, useRetrieveProductReceiptOverchargeNotice } from 'api/productReceipts/productReceipts';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { ProductReceiptsActionTypes } from './ProductReceipts.actions';
import { IProductReceiptLookupPanelHandle } from './ProductReceiptsLookupPanel/ProductReceiptsLookupPanel.properties';
import { ILineDetailsHandle } from './LineDetails/LineDetails.properties';
import LineDetails from './LineDetails';
import ProductReceiptDetails from './Details';
import { IProductReceiptDetailsHandle } from './Details/Details.properties';
import RecallParkedReceiptDialog from './RecallParkedReceiptDialog';
import FreightChargeDetails from './FreightChargeDetails';
import { IFreightChargeDetailsHandle } from './FreightChargeDetails/FreightChargeDetails.properties';
import ReceiptConfirmationDialog from './ReceiptConfirmationDialog';
import ParksaleConfirmation from './ParkReceipt';
import LoadPurchaseOrderDialog from './LoadPurchaseOrderDialog';
import OverchargeRfcDialog from './OverchargeRfcDetails';
import ReturnCreditsDialog from './ReturnCreditslDialog';
import { isScreenRequest } from 'api/utils';
import { cloneDeep } from 'lodash';
import ProcessingProblemsDialog from 'components/common/ProcessingProblemsDialog';
import { ProcessingProblemsDialogState } from 'components/common/ProcessingProblemsDialog/ProcessingProblemsDialog.properties';
import { ProcessingProblemsDialogDialogInitialState } from 'components/common/ProcessingProblemsDialog/ProcessingProblemsDialog.constants';

const ProductReceipts = (props: IProductReceiptsProps) => {
    const { classes, match, history, location, isV2 } = props;
    const params = new URLSearchParams(location.search);
    const paramaProductReceiptId = Number(params.get('ProductReceipt'));
    const [parksaleConfirmationOpen, setParksaleConfirmationOpen] = React.useState(false);
    const ProductReceiptId = usePRContextSelector<'ProductReceipt'>((state) => state.ProductReceipt);
    const ProductReceiptContext = usePRContextSelector<'ProductReceiptContext'>((state) => state.ProductReceiptContext);
    const productReceiptsLookupPanelOpen = usePRContextSelector<'ProductReceiptsLookupPanelOpen'>((state) => state.ProductReceiptsLookupPanelOpen);
    const contextDispatch = usePRDispatch();
    const tabsData = useMemo(() => getTabs(ProductReceiptContext?.ProductReceiptId, isV2), [ProductReceiptContext, isV2]);
    const { actionBarProps } = useContext(ActionBarContext);
    const url = match ? match.url : '';
    const path = match ? match.path : '';
    const linesRef = useRef<ILinesHandle>();
    const lineDetailsRef = React.useRef<ILineDetailsHandle>();
    const productReceiptsLookupPanelRef = useRef<IProductReceiptLookupPanelHandle>();
    const freightChargeDetailsRef = useRef<IFreightChargeDetailsHandle>();
    const { tabLocations } = useTabLocations(url, location);
    const fetchRecallParkedReceiptsMutation = useRetrieveParkedReceipts();
    const [loading, setLoading] = useState<boolean>(false);
    const [overChargeComment, setOverchargeComment] = useState<string>(undefined);
    const [parkedReceiptsDialogOpen, setParkedReceiptsDialogOpen] = useState(false);
    const [receiptConfirmationDialogOpen, setReceiptConfirmationDialogOpen] = useState(false);
    const { mutateAsync: applyWorksaleCriteriaMutateAsync } = useApplyProductReceiptContext();
    const { mutateAsync: parkProductReceiptMutateAsync, isLoading: isProcessing } = useParkProductReceipt();
    const { refetch: fetchProductReceiptSummary } = useRetrieveProductReceiptSummary(ProductReceiptId);
    const [loadPurchaseOrderDialogOpen, setLoadPurchaseOrderDialogOpen] = React.useState(false);
    const [returnCreditsDialog, setReturnCreditsDialog] = useState(false);
    const [overChargeRfcDialog, setOverChargeRfcDialog] = useState(false);
    const { mutateAsync: cancelProductReceiptMutateAsync } = useCancelProductReceipt();
    const useReceiptConfirmationMutation = useReceiptConfirmation();
    const useProcessReceiptMutation = useProcessReceipt();
    const useRetrieveOverchargeNoticeMutation = useRetrieveProductReceiptOverchargeNotice();
    const mismatchTotalErrorResponseText = 'Invoice Total Does Not Match.';
    const [processingProblemsDialog, setProcessingProblemsDialogDialog] = useState<ProcessingProblemsDialogState>(ProcessingProblemsDialogDialogInitialState);

    const { selectedTab, handleModuleChange } = useModule(
        history,
        location,
        'Lines',
        tabLocations,
        modules,
    );

    const detailsRef = React.useRef<IProductReceiptDetailsHandle>();

    async function loadParkedReceipts(): Promise<void> {
        try {
            setLoading(true);
            await fetchRecallParkedReceiptsMutation.mutateAsync({ ParkedReceiptsOnly: true });
        } finally {
            setLoading(false);
        }
    }

    React.useEffect(
        () => {
            if (productReceiptsLookupPanelOpen) {
                loadParkedReceipts().catch((err) => { console.log(err); });
            }
        },
        [productReceiptsLookupPanelOpen]
    );

    React.useEffect(
        () => {
            contextDispatch({
                [ProductReceiptsActionTypes.setProductReceiptsLookupPanelOpen]: !paramaProductReceiptId,
            });
        },
        [paramaProductReceiptId]
    );

    React.useEffect(
        () => {
            if (isV2 && selectedTab === 'ProductReceiptDetails') handleModuleChange('Lines');
        },

        [isV2, selectedTab]

    );

    React.useEffect(
        () => {
            contextDispatch({
                [ProductReceiptsActionTypes.setProductReceipt]: paramaProductReceiptId,
            });
            if (!paramaProductReceiptId) {
                handleModuleChange('Lines');
            }
        },
        []
    );

    React.useEffect(
        () => {
            if (ProductReceiptId !== undefined) {
                mapPropertyToURL('ProductReceipt', ProductReceiptId?.toString());
            } else {
                contextDispatch({
                    [ProductReceiptsActionTypes.setProductReceiptContext]: {},
                });
            }
        },
        [ProductReceiptId]
    );

    const onContinueCallBack = React.useCallback(
        async (actionsCallback?: () => Promise<IValidatedResponse>) => {
            if (actionsCallback) {
                const response = await actionsCallback();
                validateProcessingProblems(response, actionsCallback);
            }
        },
        []
    );

    const closeProcessingProblemsDialogDialog = React.useCallback(
        async () => {
            setProcessingProblemsDialogDialog(ProcessingProblemsDialogDialogInitialState);
        },
        [setProcessingProblemsDialogDialog, processingProblemsDialog]
    );

    const validateProcessingProblems = React.useCallback(
        async (result, actionsCallback?: () => Promise<IValidatedResponse>) => {
            if (result?.Messages && result?.Messages.length > 0) {
                setProcessingProblemsDialogDialog(
                    {
                        open: true,
                        messages: result?.Messages,
                        description: 'The issues listed below have been identified during processing. Any errors found must be corrected before you can continue.\n Select BACK to return to the Fulfillment screen. Warning and Errors are highlighted in the fulfillment grid.',
                        onContinue: () => { onContinueCallBack(actionsCallback); },
                        onBack: () => {
                            closeProcessingProblemsDialogDialog();
                            setReceiptConfirmationDialogOpen(false);
                            contextDispatch({
                                [ProductReceiptsActionTypes.setProductReceiptsLookupPanelOpen]: false
                            });
                            linesRef.current.refreshDataGrid(result?.Messages);
                        }
                    });
            } else {
                closeProcessingProblemsDialogDialog();
            }
        },
        []
    );

    const mapPropertyToURL = (name: string, value: string) => {
        const currentUrl = window.location.href;
        const urlData = new URL(currentUrl);

        urlData.searchParams.set(name, value);
        window.history.replaceState({}, '', urlData);
    };

    const _handleModuleChange = React.useCallback(
        (tabToNavigate, inlineObject?: ILocationState) => {
            if (!ProductReceiptId) return;
            switch (selectedTab) {
                case 'ProductReceiptDetails':
                    if (detailsRef?.current?.isChangesSavedOrDiscarded()) {
                        handleModuleChange(tabToNavigate, inlineObject ?? { search: { ProductReceipt: ProductReceiptId } });
                    }
                    break;
                case 'FreightChargeDetails':
                    if (freightChargeDetailsRef?.current?.isChangesSavedOrDiscarded()) {
                        handleModuleChange(tabToNavigate, inlineObject ?? { search: { ProductReceipt: ProductReceiptId } });
                    }
                    break;
                default:
                    handleModuleChange(tabToNavigate, inlineObject ?? { search: { ProductReceipt: ProductReceiptId } });
            }
        },
        [selectedTab, ProductReceiptContext, handleModuleChange, freightChargeDetailsRef, ProductReceiptId]
    );

    const handleModuleChangeExtended = React.useCallback(
        (tabToNavigate) => {
            if (DisabledChangeStatus.includes(tabToNavigate)) {
                contextDispatch({
                    [ProductReceiptsActionTypes.setIsChangeStatusDisabled]: true,
                });
            } else {
                contextDispatch({
                    [ProductReceiptsActionTypes.setIsChangeStatusDisabled]: false,
                });
            }

            handleModuleChange(tabToNavigate);
        },
        [selectedTab, ProductReceiptContext, handleModuleChange]
    );

    const cancelProductReceipt = async () => {
        const response = await cancelProductReceiptMutateAsync({ ProductReceiptId });
        if (response?.Status) {
            clearProductReceiptContext();
            await loadParkedReceipts();
        }
    };

    const handleActionBarButtonClick = React.useCallback(
        (action: string) => {
            switch (action) {
                case 'Back':
                    switch (selectedTab) {
                        case 'Details':
                            _handleModuleChange('Lines');
                            break;
                        default:
                    }
                    break;
                case 'Process':
                    initializeProcessProductReceipt();
                    break;
                case 'RecallReceipt':
                    setParkedReceiptsDialogOpen(true);
                    break;
                case 'ProductReceiptDetailsOk':
                case 'ProductReceiptDetailsCancel':
                    detailsRef.current?.[action](() => { handleModuleChangeExtended('Lines'); });
                    break;

                case 'ProductReceiptLineDetailsOk':
                case 'ProductReceiptLineDetailsCancel':
                    lineDetailsRef.current?.[action](() => { handleModuleChangeExtended('Lines'); });
                    break;

                case 'ProductReceiptLineDetailsPutAway':
                    lineDetailsRef.current?.[action]();
                    break;

                case 'FreightChargeDetailsOk':
                case 'FreightChargeDetailsCancel': {
                    freightChargeDetailsRef.current?.[action]();
                    break;
                }
                case 'ParkReceipt':
                    setParksaleConfirmationOpen(true);
                    break;

                case 'Load':
                    setLoadPurchaseOrderDialogOpen(true);
                    break;

                case 'CancelReceipt':
                    cancelProductReceipt();
                    break;
                default:
            }
        },
        [selectedTab, _handleModuleChange, linesRef]
    );

    async function handleApplyProductReceiptContext(criteria: IProductReceiptContextPageFacade): Promise<IObjectifiedProductReceiptContextResponse> {
        try {
            setLoading(true);

            const updatedCriteria = cloneDeep(criteria);
            if (!updatedCriteria?.Freight) {
                updatedCriteria.Freight = 0;
            }
            if (!updatedCriteria?.InvoiceTotal) {
                updatedCriteria.InvoiceTotal = 0;
            }

            const response = await applyWorksaleCriteriaMutateAsync({ ProductReceiptContext: { ...updatedCriteria }, ProductReceiptId: updatedCriteria.ProductReceiptId !== 0 ? updatedCriteria.ProductReceiptId : (ProductReceiptId || 0) });
            if (response?.Status && response?.ProductReceiptContext) {
                if (ProductReceiptId) {
                    fetchProductReceiptSummary();
                    if (selectedTab === 'FreightChargeDetails') freightChargeDetailsRef?.current?.refetchFreightChargeDetails();
                }

                showSnackBar({ variant: 'success', message: 'Product Receipt changes applied successfully.' });
                contextDispatch({
                    [ProductReceiptsActionTypes.setProductReceipt]: response.ProductReceiptContext?.inlineObject?.ProductReceiptId,
                });
                contextDispatch({
                    [ProductReceiptsActionTypes.setProductReceiptContext]: response.ProductReceiptContext?.inlineObject,
                });
            }

            return response;
        } finally {
            setLoading(false);
        }
    }

    // Handle Park Sale Confirmation
    async function handleOkParksaleConfirmation(query: any): Promise<void> {
        try {
            const response = await parkProductReceiptMutateAsync({ ProductReceiptId, Note: query?.Note });

            if (response?.Status) {
                setParksaleConfirmationOpen(false);
                showSnackBar({ variant: 'success', message: 'Parked Product Receipt successfully.' });
                clearProductReceiptContext();
                await loadParkedReceipts();
            }

        } catch (err) {
            showSnackBar({ variant: 'error', message: `Unexpected Error ${err.message}.` });
        }
    }

    const clearProductReceiptContext = () => {
        contextDispatch(
            {
                [ProductReceiptsActionTypes.setProductReceipt]: 0,
                [ProductReceiptsActionTypes.setProductReceiptContext]: {}
            });
        linesRef.current.fastEntryResetToDefault();

        contextDispatch({
            [ProductReceiptsActionTypes.setProductReceiptsLookupPanelOpen]: true
        });
    };

    const initializeProcessProductReceipt = React.useCallback(
        async () => {
            const response = await useRetrieveOverchargeNoticeMutation.mutateAsync({ ProductReceiptId });

            if (response?.Status && isScreenRequest(response?.Forms, 'OverchargeNotice')) {
                setOverChargeRfcDialog(true);

                return;
            } else if (response?.Status) {
                processConfirmationDialog();
            }
        },
        [ProductReceiptId]
    );

    const processConfirmationDialog = React.useCallback(
        async () => {

            const response = await useReceiptConfirmationMutation.mutateAsync({ ProductReceiptId });
            if (response?.Status) {
                setReceiptConfirmationDialogOpen(true);
            }
        },
        [ProductReceiptId]
    );

    const processProductReceipts = React.useCallback(
        async (data: IProcessProductReceiptCriteriaFacade) => {

            const response = await useProcessReceiptMutation.mutateAsync(
                {
                    ProductReceiptId,
                    ...data,
                    OverChargeComment: overChargeComment
                });

            if (response?.Status && response?.ProcessProductReceiptConfirmation?.length > 0) {
                setReceiptConfirmationDialogOpen(false);
                setReturnCreditsDialog(true);
            } else if (response?.Status) {
                await onProcessedProductReceipts();
            } else if (!response?.Status && response?.Forms?.length > 0) {
                response?.Forms?.map((form) => {
                    if (form.MessageType === 'Error' && form.Title === mismatchTotalErrorResponseText) {
                        setReceiptConfirmationDialogOpen(false);
                        contextDispatch({
                            [ProductReceiptsActionTypes.setProductReceiptsLookupPanelOpen]: true,
                            [ProductReceiptsActionTypes.setLHSFocusField]: 'InvoiceTotal'
                        });
                    }
                }
                );
            }
            validateProcessingProblems(response);

        },
        [ProductReceiptId, overChargeComment]
    );

    const onProcessedProductReceipts = async () => {
        setReceiptConfirmationDialogOpen(false);
        clearProductReceiptContext();
        await loadParkedReceipts();
    };

    return (
        <div className={classes.mainDiv}>
            <div className={classes.innerContainer}>
                <ProductReceiptsLookupPanel
                    ref={productReceiptsLookupPanelRef}
                    open={productReceiptsLookupPanelOpen}
                    loading={loading}
                    parkedReceiptsItems={fetchRecallParkedReceiptsMutation.data?.ParkedProductReceipt}
                    parkedReceiptsItemsLoading={fetchRecallParkedReceiptsMutation.isLoading}
                    onOpenChanged={(open) => {
                        contextDispatch({
                            [ProductReceiptsActionTypes.setProductReceiptsLookupPanelOpen]: open,
                        });
                    }}
                    onApplyProductReceiptContext={handleApplyProductReceiptContext}
                />
                <ParksaleConfirmation
                    open={parksaleConfirmationOpen}
                    onCancel={() => { setParksaleConfirmationOpen(false); }}
                    onOk={handleOkParksaleConfirmation}
                    isProcessing={isProcessing}
                />
                <div
                    className={classNames(classes.PSSection, {
                        [classes.PSSectionMarginOpen]: productReceiptsLookupPanelOpen,
                        [classes.PSSectionMarginClose]: !productReceiptsLookupPanelOpen
                    })}
                >
                    <BreadcrumbBar
                        mainModule={'Product Receipts'}
                        activeModuleId={selectedTab}
                        moduleTree={modules}
                        onBreadcrumbClick={_handleModuleChange}
                        onMainModuleClick={() => _handleModuleChange('Lines')}
                        hideOptionsMenu={true}
                    />
                    <SummaryPanel />
                    {tabsData.some(({ id }) => id === selectedTab) && <NavigationTabs
                        lookupOpen={productReceiptsLookupPanelOpen}
                        tabsData={tabsData}
                        activeTabId={selectedTab}
                        onTabChange={_handleModuleChange}
                    />}
                    <Route
                        key={'Lines'}
                        path={path}
                        exact
                        render={() => <Lines forwardRef={linesRef} changeSelectedTab={_handleModuleChange} />}
                    />
                    <Route
                        key={'LineDetails'}
                        path={`${path.endsWith('/') ? path : `${path}/`}line-details`}
                        exact
                        render={(routeProps) => <LineDetails {...routeProps} innerRef={lineDetailsRef} changeSelectedTab={_handleModuleChange} />}
                    />
                    <Route
                        key={'ProductReceiptDetails'}
                        path={`${path.endsWith('/') ? path : `${path}/`}product-receipt-details`}
                        exact
                        render={() => <ProductReceiptDetails innerRef={detailsRef} />}
                    />
                    <Route
                        key={'FreightChargeDetails'}
                        path={`${path.endsWith('/') ? path : `${path}/`}freight-charge-details`}
                        exact
                        render={() => <FreightChargeDetails innerRef={freightChargeDetailsRef} changeSelectedTab={handleModuleChange} />}
                    />
                    <RecallParkedReceiptDialog
                        open={parkedReceiptsDialogOpen}
                        onRecall={() => {
                            setParkedReceiptsDialogOpen(false);
                            contextDispatch({
                                [ProductReceiptsActionTypes.setProductReceiptsLookupPanelOpen]: false,
                            });
                        }}
                        onCancel={() => setParkedReceiptsDialogOpen(false)} />
                    {
                        loadPurchaseOrderDialogOpen ? (
                            <LoadPurchaseOrderDialog
                                open={loadPurchaseOrderDialogOpen}
                                initialValues={{}}
                                valuesSchema={{}}
                                onClose={() => setLoadPurchaseOrderDialogOpen(false)}
                                linesRef={linesRef}
                            />
                        ) : undefined
                    }

                    <OverchargeRfcDialog
                        open={overChargeRfcDialog}
                        onCancel={() => { setOverChargeRfcDialog(false); }}
                        isProcessing={useReceiptConfirmationMutation.isLoading}
                        onOk={(comment: any) => {
                            setOverChargeRfcDialog(false);
                            setOverchargeComment(comment);
                            processConfirmationDialog();
                        }}
                        initialValues={useRetrieveOverchargeNoticeMutation?.data?.OverchargeNotice}
                    />

                    <ReturnCreditsDialog
                        open={returnCreditsDialog}
                        initialValues={useProcessReceiptMutation?.data?.ProcessProductReceiptConfirmation}
                        onClose={() => { setReturnCreditsDialog(false); onProcessedProductReceipts(); }}
                    />

                    <RecallParkedReceiptDialog
                        open={parkedReceiptsDialogOpen}
                        onRecall={() => {
                            setParkedReceiptsDialogOpen(false);
                            contextDispatch({
                                [ProductReceiptsActionTypes.setProductReceiptsLookupPanelOpen]: false,
                            });
                        }}
                        onCancel={() => setParkedReceiptsDialogOpen(false)} />
                    <ReceiptConfirmationDialog
                        open={receiptConfirmationDialogOpen}
                        onCancel={() => { setReceiptConfirmationDialogOpen(false); }}
                        initialValues={useReceiptConfirmationMutation?.data?.ProcessProductReceiptConfirmation}
                        isProcessing={useProcessReceiptMutation.isLoading}
                        onOk={processProductReceipts}
                        supplierId={Number(ProductReceiptContext?.SupplierId) || 0}
                    />
                    <ProcessingProblemsDialog
                        open={processingProblemsDialog.open}
                        messages={processingProblemsDialog.messages}
                        onBack={processingProblemsDialog.onBack}
                        onContinue={processingProblemsDialog.onContinue}
                        dialogTitle={processingProblemsDialog.dialogTitle}
                        description={processingProblemsDialog.description}
                    />

                </div>
            </div>
            <ActionBar {...actionBarProps} onActionClick={handleActionBarButtonClick} />
        </div >
    );
};

export default withRouter(withStyles(styles, { index: 1 })(ProductReceipts));
